import { useCallback } from 'react'

import { useNavigate } from '@lib/react-router-dom'

import { useAccountReadContext } from '@context/AccountContextContext'
import { useProductWriteContext } from '@context/ProductContext'

import { hasProductActivatedStatus } from '@utils/AccountAccess/accountProductStatuses'
import { isTickmillPartner } from '@utils/companyName.utils'
import { navigateBasedOnProductType } from '@utils/path'

import {
  TickmillProductType,
  isTickmillProductTypeCFD,
  isTickmillProductTypeETD,
} from '@model/TickmillProductType'

import { useLandingPage } from '@hooks/useLandingPage'

const getAlternativeBaseProduct = (
  product: TickmillProductType
): TickmillProductType | undefined => {
  if (isTickmillProductTypeCFD(product)) {
    return TickmillProductType.ETD
  }

  if (isTickmillProductTypeETD(product)) {
    return TickmillProductType.CFD
  }

  return undefined
}

export const useProductRegistrationExit = (product: TickmillProductType): (() => void) => {
  const navigate = useNavigate()
  const { account } = useAccountReadContext()
  const { changeProductContext } = useProductWriteContext()
  const { getLandingPage } = useLandingPage()
  const isPartner = isTickmillPartner(account)

  return useCallback(() => {
    const alternativeProduct = getAlternativeBaseProduct(product)

    if (alternativeProduct && hasProductActivatedStatus(account, alternativeProduct)) {
      changeProductContext(alternativeProduct)
      navigate(getLandingPage(alternativeProduct))
      return
    }

    if (isTickmillProductTypeCFD(product) || isTickmillProductTypeETD(product)) {
      navigateBasedOnProductType(isTickmillProductTypeCFD(product), navigate, isPartner)
      return
    }

    navigate(getLandingPage())
  }, [account, changeProductContext, getLandingPage, isPartner, navigate, product])
}
